import axios from 'axios';
import { BASE } from './constants';

const API_BASE = `${BASE}/api/v1`;

// Token storage (localStorage for persistence across tabs/refreshes)
const TOKEN_KEY = 'agentcfo_access_token';
const REFRESH_KEY = 'agentcfo_refresh_token';
const USER_KEY = 'agentcfo_user';

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const getStoredRefresh = () => localStorage.getItem(REFRESH_KEY);
export const getStoredUser = (): any => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
};

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor: attach token
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue: Array<{ resolve: (t: string) => void; reject: (e: any) => void }> = [];

const processQueue = (error: any, token: string | null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for 401s that aren't refresh/login requests themselves
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = getStoredRefresh();
      if (!refreshToken || originalRequest.url?.includes('/auth/')) {
        clearAuth();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(`${API_BASE}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const newToken = res.data.access_token;
        localStorage.setItem(TOKEN_KEY, newToken);
        // Store rotated refresh token if server provides one (AUTH_ENABLED=true)
        if (res.data.refresh_token) {
          localStorage.setItem(REFRESH_KEY, res.data.refresh_token);
        }
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuth();
        // Redirect to login on refresh failure
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Legacy compatibility
let token: string | null = null;
export const setToken = (t: string) => {
  token = t;
  localStorage.setItem(TOKEN_KEY, t);
};
export const getToken = () => token || getStoredToken();

/**
 * Login and store tokens + user info.
 * Returns { access_token, refresh_token, user }
 */
export const login = async (email: string, password: string) => {
  const res = await axios.post(`${API_BASE}/auth/token`, { email, password });
  const { access_token, refresh_token, user } = res.data;
  localStorage.setItem(TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  token = access_token;
  return res.data;
};

export const getInvoices = async () => {
  const res = await api.get('/invoices');
  return res.data;
};

export const getInvoice = async (id: number) => {
  const res = await api.get(`/invoices/${id}`);
  return res.data;
};

export const uploadInvoice = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/invoices/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
};

export const processSmartInvoice = async (id: number, persona: string = 'ap_accountant') => {
  const res = await api.post(`/${id}/process-smart?persona=${persona}`);
  return res.data;
};

export const getPersonas = async () => {
  const res = await api.get('/agents/personas');
  return res.data;
};

export const getSkills = async () => {
  const res = await api.get('/agents/skills');
  return res.data;
};

export default api;
